import { ReactNode, SetStateAction, Dispatch } from 'react'
import { Col, Row } from 'antd'
import PartStatusCell from '@Orders/OrderDetailsPage/Status/OrderedItems/components/SalesOrderLinesTable/PartStatusCell'
import RenderShipping from '@Orders/OrderDetailsPage/Status/OrderedItems/components/SalesOrderLinesTable/RenderShipping'
import QualityDocsAndPhotosCell from '@Orders/OrderDetailsPage/Status/OrderedItems/components/SalesOrderLinesTable/QualityDocsAndPhotosCell'
import {
    OrderedItemsRecordType,
    OnViewInspectionButtonClickFuncArgsType,
} from '@Orders/OrderDetailsPage/Status/OrderedItems/components/types'
import { ToggleModalForPartViewerFuncType } from '@Orders/OrderDetailsPage/Status/OrderedItems/components/SalesOrderLinesTable/logic'
import { shipmentIdsMapType } from '@Orders/OrderDetailsPage/logic'
import { salesOrder_salesOrder_shipments_items as SalesOrderShipmentItemsType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { ColumnType } from 'antd/lib/table'

import Image from '~/core/Image'
import getColor from '~/lib/getColor'
import { ORDERED_QUANTITY, PROCESS_NAME } from '~/lib/partsConstants'

import PartsAndSamples from './PartsAndSamples'
import { processDisplayName } from './logic'

const PLACEMENT = 'im-order-table'

type ImOrderPropsType = {
    isImOrder: boolean
    isEnabledForIm?: boolean
    orderName: string
    shippedBy: string
    getDescriptionData: (
        processName: string,
        record: OrderedItemsRecordType,
    ) => ReactNode
    shipmentIdsMap: shipmentIdsMapType
    toggleModalForPartViewer: ToggleModalForPartViewerFuncType
    partItems: OrderedItemsRecordType[]
    onViewInspectionButtonClick: (
        args: OnViewInspectionButtonClickFuncArgsType,
    ) => void
    salesOrderShipmentItems?: SalesOrderShipmentItemsType[]
    moldStatus: string | null
    setSelectedTab?: Dispatch<SetStateAction<string>>
}

export default function ImOrderColumns(
    props: ImOrderPropsType,
): ColumnType<OrderedItemsRecordType>[] {
    const {
        isImOrder,
        isEnabledForIm,
        orderName,
        shippedBy,
        shipmentIdsMap,
        partItems,
        salesOrderShipmentItems,
        moldStatus,
        getDescriptionData,
        toggleModalForPartViewer,
        onViewInspectionButtonClick,
        setSelectedTab,
    } = props

    const getPartsAndSamples = (record: OrderedItemsRecordType) => {
        return (
            <Row>
                <Col span={5}>
                    <Image
                        style={{
                            width: '48px',
                            height: '48px',
                            background: 'white',
                            border: `1px solid ${getColor('blackAlpha15')}`,
                        }}
                        src={record?.revision?.thumbnailUri}
                    />
                </Col>
                <Col span={14}>
                    <PartsAndSamples
                        isImOrder={isImOrder}
                        record={record}
                        orderName={orderName}
                        isEnabledForIm={isEnabledForIm}
                        placement={PLACEMENT}
                        getDescriptionData={getDescriptionData}
                        toggleModalForPartViewer={toggleModalForPartViewer}
                        setSelectedTab={setSelectedTab}
                    />
                </Col>
            </Row>
        )
    }

    const columns: ColumnType<OrderedItemsRecordType>[] = [
        {
            title: 'Parts and samples',
            key: 'revision',
            fixed: 'left',
            width: '25%',
            render: (record: OrderedItemsRecordType, row, index) => {
                const partsData = {
                    children: getPartsAndSamples(
                        partItems[partItems.length - 1],
                    ),
                    props: { rowSpan: 1 },
                }

                if (index === 0) {
                    partsData.props.rowSpan = partItems.length
                }

                if (index > 0) {
                    partsData.props.rowSpan = 0
                }
                return partsData
            },
        },
        {
            title: 'Description',
            dataIndex: PROCESS_NAME,
            key: PROCESS_NAME,
            render: processDisplayName,
            width: '15%',
        },
        {
            title: 'Quantity',
            width: '240px',
            align: 'center',
            dataIndex: ORDERED_QUANTITY,
            key: ORDERED_QUANTITY,
        },
        {
            title: 'Part status',
            key: 'productionStatus',
            align: 'left',
            width: '250px',
            render: (record: OrderedItemsRecordType) => (
                <PartStatusCell moldStatus={moldStatus} {...{ record }} />
            ),
        },
        {
            title: 'Quality docs. and photos',
            key: 'qualityDocsAndPhotos',
            align: 'center' as const,
            width: '250px',
            render: (record: OrderedItemsRecordType) => (
                <QualityDocsAndPhotosCell
                    {...{ record, onViewInspectionButtonClick }}
                />
            ),
        },
        {
            title: 'Shipping',
            key: 'shipping',
            align: 'center',
            width: '280px',
            render: (record: OrderedItemsRecordType) =>
                record && (
                    <RenderShipping
                        processes={record.processes}
                        processName={record.processName}
                        isImOrder={isImOrder}
                        status={record.status}
                        shippedBy={shippedBy}
                        orderedQuantity={record.orderedQuantity}
                        deliveredQuantity={record.deliveredQuantity}
                        shipments={record.shipments}
                        shipmentIdsMap={shipmentIdsMap}
                        toggleModalForPartViewer={toggleModalForPartViewer}
                        shipmentsData={salesOrderShipmentItems}
                    />
                ),
        },
    ]

    return columns
}
